import React, { useEffect, useMemo, useState } from "react";
import SecretExpiryNifty from "./secret-expiry/SecretExpiryNifty";
import SecretExpirySensex from "./secret-expiry/SecretExpirySensex";
import SecretExpiryBankNifty from "./secret-expiry/SecretExpiryBankNifty";
import SecretExpiryBankex from "./secret-expiry/SecretExpiryBankex";
import SecretExpiryJammu from "./secret-expiry/SecretExpiryJammu";
import OptionDecayDesk from "./OptionDecayDesk";
import SecretExpiryWriterMatrix from "./secret-expiry/SecretExpiryWriterMatrix";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const TOKEN_KEY = "ct_login_token";

const C = {
  bg: "#04090f",
  panel: "#080f1a",
  border: "#f7c94855",
  gold: "#ffd84a",
  dimGold: "#9f7a24",
  green: "#006b49",
  red: "#7f0013",
};

const TABS = [
  { id: "nifty", label: "NIFTY" },
  { id: "sensex", label: "SENSEX" },
  { id: "banknifty", label: "BANKNIFTY" },
  { id: "bankex", label: "BANKEX" },
  { id: "jammu", label: "JAMMU" },
  { id: "writer-matrix", label: "WRITER MATRIX" },
  { id: "expiry-decay", label: "EXPIRY DECAY" },
];

export default function SecretExpiryDesk() {
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("secret_expiry_desk_active_tab") || "nifty";
  });

  const [secretExpiryRefreshSec, setSecretExpiryRefreshSec] = useState(5);

  useEffect(() => {
  const loadRefreshSetting = async () => {
      const res = await fetch(`${API_BASE}/api/secret-expiry/settings?t=${Date.now()}`, {
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        setSecretExpiryRefreshSec(Math.max(1, Number(data.secretExpiryDeskRefreshSeconds || 5)));
      }
    };

    loadRefreshSetting().catch(() => {});
  }, []);

  const [refreshSec, setRefreshSec] = useState(() => {
    return Math.max(5, Number(localStorage.getItem("secret_expiry_desk_refresh_sec") || 5));
  });

  useEffect(() => {
    localStorage.setItem("secret_expiry_desk_active_tab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem("secret_expiry_desk_refresh_sec", String(refreshSec));
  }, [refreshSec]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    const loadSettings = async () => {
      const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) return;

      const settings = data.settings || {};
      setRefreshSec(Math.max(5, Number(settings.bloombergRefreshSeconds || 5)));
    };

    loadSettings().catch(() => {});
  }, []);

  const activeLabel = useMemo(() => {
    return TABS.find(tab => tab.id === activeTab)?.label || "NIFTY";
  }, [activeTab]);

  const tabButtonStyle = tab => {
    const active = activeTab === tab.id;

    return {
      minWidth: 140,
      padding: "18px 26px",
      border: `1px solid ${active ? "#00b37a" : C.border}`,
      borderRadius: 4,
      background: active ? C.green : "#050505",
      color: active ? "#ffffff" : C.gold,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
      fontSize: 13,
      fontWeight: 800,
      letterSpacing: 3,
      cursor: "pointer",
      boxShadow: active ? "0 0 18px rgba(0,179,122,0.45)" : "0 0 14px rgba(255,216,74,0.12)",
    };
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        color: C.gold,
        fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
      }}
    >
      <div
        style={{
          padding: "26px 24px 18px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          gap: 20,
        }}
      >
        <div>
          <div style={{ fontSize: 12, letterSpacing: 8, fontWeight: 900 }}>
            NEXOVATE OPTION EXPIRY TERMINAL
          </div>

          <h1
            style={{
              margin: "12px 0 8px",
              fontSize: 42,
              lineHeight: 1,
              color: "#ffe27a",
              textShadow: "0 0 18px rgba(255,216,74,0.55)",
            }}
          >
            Expiry Decay Desk
          </h1>

          <div style={{ fontSize: 14, color: C.gold }}>
            NSE / BSE option-chain source, Secret page scanners, OI analysis, and Jammu analytics.
          </div>
        </div>

        <div
          style={{
            textAlign: "right",
            fontSize: 12,
            letterSpacing: 4,
            fontWeight: 900,
            color: C.gold,
            textShadow: "0 0 12px rgba(255,216,74,0.6)",
          }}
        >
          <div>ACTIVE: {activeLabel}</div>
          <div>REFRESH: {refreshSec}s</div>
        </div>
      </div>

      <div
        style={{
          padding: "18px 24px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          background: "#020202",
        }}
      >
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={tabButtonStyle(tab)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        style={{
          padding: "8px 24px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          fontSize: 11,
          letterSpacing: 3,
          background: "#060d18",
        }}
      >
        {TABS.map(tab => (
          <span key={tab.id}>
            <span style={{ color: activeTab === tab.id ? "#22c55e" : C.dimGold }}>●</span>{" "}
            {tab.label}: {activeTab === tab.id ? "OPEN" : "READY"}
          </span>
        ))}
      </div>

      <div style={{ padding: "24px", maxWidth: 1680, margin: "0 auto" }}>
        <div style={{ display: activeTab === "nifty" ? "block" : "none" }}>
          <SecretExpiryNifty refreshSec={secretExpiryRefreshSec} />
        </div>

        <div style={{ display: activeTab === "sensex" ? "block" : "none" }}>
          <SecretExpirySensex refreshSec={secretExpiryRefreshSec} />
        </div>

        <div style={{ display: activeTab === "banknifty" ? "block" : "none" }}>
          <SecretExpiryBankNifty refreshSec={secretExpiryRefreshSec} />
        </div>

        <div style={{ display: activeTab === "bankex" ? "block" : "none" }}>
          <SecretExpiryBankex refreshSec={secretExpiryRefreshSec} />
        </div>

        <div style={{ display: activeTab === "jammu" ? "block" : "none" }}>
          <SecretExpiryJammu refreshSec={secretExpiryRefreshSec} />
        </div>

        <div style={{ display: activeTab === "writer-matrix" ? "block" : "none" }}>
          <SecretExpiryWriterMatrix refreshSec={secretExpiryRefreshSec} />
        </div>

        <div style={{ display: activeTab === "expiry-decay" ? "block" : "none" }}>
          <OptionDecayDesk />
        </div>

      </div>
    </div>
  );
}